import { useMemo } from 'react';
import type {
  DomainGroup,
  DomainKey,
  DomainProvenance,
  NodeKey,
  NodeSource,
  NodeUnit,
  SlotInputNode,
  SlotValuationInputsResponse,
  ZoneKey,
  ZoneMeta,
} from 'types/packages-api/slot-valuation-inputs';
import { useModelMap } from './hooks';
import { AC13_OUTPUT_KEYS, ZONE_LABELS, ZONE_ORDER } from './utils';
import {
  buildSourceBlocks,
  seriesColumnsForZone,
  withOwnershipRelocation,
  type ExpenseClass,
  type SourceBlock,
  type VolumeSourceKind,
} from './source-blocks';
import { isNodeEditable } from './editability';

/** One node's served definition fields that the table renders from. */
export interface NodeMeta {
  unit?: NodeUnit;
  /** The registry's own display label. The Calculated result was humanising the KEY. */
  label?: string;
  /** The reserve category, on the twelve base rates and the four schedule functions. */
  category?: string;
  /** The registry's reader note (ModelMap::READER_NOTES), on a node that carries one (none today). */
  note?: string;
  /**
   * Where the value is read from, or `declared_only` when there is nowhere.
   *
   * THE ONLY THING THAT SEPARATES "NEVER GOING TO HAVE A VALUE" FROM "NOT RECORDED", and
   * both used to draw the same dash. 28 of the 221 nodes are `declared_only` and are empty
   * on every slot by construction; 7 more have a real source and were empty on all 45 valued
   * slots measured. Read through valueSourceState, never by testing the shape here.
   */
  source?: NodeSource;
  /**
   * The override engine's field name, ABSENT on the 167 nodes that carry none.
   *
   * Rides the node DEFINITION rather than the slot row, so it arrives with the model map's
   * second request and not with the first paint. Never test it here — read it through
   * editability.isNodeEditable, which is the only place the edit rule lives.
   */
  overridable?: string;
  /**
   * The OverrideFields key a write must use, ABSENT wherever `overridable` is. Read through
   * editability.overrideFieldKeyFor, never spelled out at a call site.
   */
  overrideKey?: string;
  /**
   * The `dates` entry carrying this node's per-slot lock — the four schedule dates only.
   * Read through editability.isNodeLocked / nodeKeyForMilestone.
   */
  milestone?: string;
  /**
   * Whether an override on this node moves the value this row shows. Served on every node;
   * read through override-badge.rowCanCarryOverride.
   */
  overrideReachesRow?: boolean;
}

/** Node key → its served definition fields. Empty until the model map resolves. */
export type MetaByNode = Partial<Record<NodeKey, NodeMeta>>;

/** Zone key → its served metadata. Carries `domain`, which the edit gate needs. */
export type ZonesByKey = Partial<Record<ZoneKey, ZoneMeta>>;

/**
 * The served node definitions, keyed by node — the unit and the reserve category.
 *
 * ── WHY A SECOND REQUEST, AND WHY IT DOES NOT BLOCK THE FIRST PAINT ──────────────────
 *
 * Both ride on the NODE DEFINITION, not on each resolved value: a definition travels once
 * per deploy while values travel per slot, so putting either on a value would repeat it
 * thousands of times and create a second copy that could disagree. That is why they are on
 * `POST /queries/model_map` and not in this response.
 *
 * The table reads them through the SAME react-query key the diagram uses, so the two views
 * share one cache entry and one request. It is NOT awaited: until the map lands every field
 * is undefined and the table renders exactly as it would from the tearsheet response alone,
 * which is contract note 1's promise. Cached against `meta.registryDigest`, so once per
 * session.
 *
 * @return {MetaByNode} The definition fields per node key, or an empty map while loading.
 */
export function useNodeMeta(): {
  byKey: MetaByNode;
  /** Zone metadata, for the edit gate's coarse half. Empty while the map is loading. */
  zones: ZonesByKey;
  resolved: boolean;
  failed: boolean;
} {
  const { data, isError } = useModelMap();

  return useMemo(() => {
    if (!data?.nodes) {
      // THREE STATES, NOT TWO. "not arrived yet" (retries still pending) and a TERMINAL
      // failure (POST /queries/model_map exhausted its retries — the map will never arrive)
      // both land here with no nodes, and a caller that cannot tell them apart shows
      // PendingValue forever on a permanent failure and leaves the Export button disabled
      // with no explanation. `failed` is that third state; `resolved` stays the "arrived"
      // signal so the not-yet path renders exactly as before. See PendingValue, the
      // TearsheetTableView notice, and TearsheetExportButton.
      return { byKey: {}, zones: {}, resolved: false, failed: isError };
    }
    return {
      byKey: Object.fromEntries(
        Object.entries(data.nodes).map(([key, node]) => [
          key,
          {
            unit: node.unit,
            category: node.category,
            label: node.label,
            source: node.source,
            // SPREAD, NOT `overridable: node.overridable`. Assigning it unconditionally
            // would put `overridable: undefined` on all 167 unoverridable nodes, and
            // `'overridable' in node` — the test the contract mandates — is TRUE for a key
            // present with an undefined value. That single line would have made every node
            // on the page report as editable.
            ...('overridable' in node ? { overridable: node.overridable } : {}),
            // SPREAD FOR THE SAME REASON, and for both. `overrideKey` rides with
            // `overridable`; `milestone` is on the four schedule dates alone. Assigning
            // either unconditionally would put the key present-with-undefined on every other
            // node, and the readers test with `in` exactly as the contract says to.
            ...('overrideKey' in node ? { overrideKey: node.overrideKey } : {}),
            ...('milestone' in node ? { milestone: node.milestone } : {}),
            // Sparse on the server (INS-13307), and spread for the same reason as the two above.
            ...('note' in node ? { note: node.note } : {}),
            // NOT spread: this one is served on EVERY node, so a missing key means the map
            // predates it rather than that the node has no answer — which is the case
            // rowCanCarryOverride's fallback is for.
            overrideReachesRow: node.overrideReachesRow,
          },
        ])
      ) as MetaByNode,
      zones: (data.zones ?? {}) as ZonesByKey,
      resolved: true,
      failed: false,
    };
  }, [data, isError]);
}

/**
 * The node keys AC 13's Calculated result owns, so no zone table renders them twice.
 *
 * ONE DEFINITION, because two callers need the SAME answer: buildSections uses it to drop
 * rows, and the page uses it to decide which series columns are disclosed. Computing it
 * twice from different ingredients is how the two quietly disagree — and a disagreement
 * here means a node loses its inline preview without gaining a column.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @return {Set<string>} Keys the Calculated result renders.
 */
export function outputsOwnedKeys(
  response: SlotValuationInputsResponse
): Set<string> {
  return new Set<string>([
    ...AC13_OUTPUT_KEYS,
    ...Object.keys(response.outputs?.values ?? {}),
  ]);
}

/** A rendered section: one layout zone's inputs, with its scenario/area context. */
export interface ZoneSection {
  zone: ZoneKey;
  /** The domain this zone joined to, or null for the three zones that have none. */
  domain: DomainKey | null;
  label: string;
  scenario: string | null;
  /** Carried so a link can render on the id where a run recorded no name. */
  scenarioId: number | string | null;
  area: string | null;
  /** Carried whole, so a BLANK domain can still say why it is blank (spec 5.4). */
  provenance: DomainProvenance | null;
  nodes: SlotInputNode[];
  /**
   * Path to the scenario that supplied these values, or null when there is nowhere to go.
   * NULL IS NORMAL, but the list of who it is normal FOR has shrunk twice: price_deck gained
   * a link, and then royalty and expenses did (both are supplied by the
   * run's legacy `Scenario`, which has a page). It is null today for royalty, expenses or
   * price_deck only on a run whose source could not be read, and for the three zones with no
   * domain at all. A link renders only when this is set — never built from the key or name.
   */
  scenarioHref: string | null;
  /** Set on the type-curve block only — what this slot's volumes actually came from. */
  volumeSource: VolumeSourceKind | null;
  /** Set on the expenses block only. Derived from the registry, never listed. */
  groups: Array<{ expenseClass: ExpenseClass; nodes: SlotInputNode[] }>;
  /** This block's monthly columns, derived from each column node's own declared zone. */
  seriesColumns: NodeKey[];
  /**
   * The nodes in this block the user may edit.
   *
   * The JOIN of each node's registry `overridable` and this zone's DomainGroup `readOnly`,
   * computed by editability.isNodeEditable — the only place that rule lives. Read this set;
   * do not re-test `overridable` or `readOnly` anywhere else.
   *
   * ONE SET FEEDS BOTH SCALES. The block marker asks whether it is non-empty and each row
   * asks whether it contains that row's key, so the marker cannot promise an affordance no
   * row offers, nor a row offer one the block did not announce. Deriving them separately is
   * how those two drift.
   *
   * EMPTY until the model map resolves, because `overridable` rides the node definition and
   * not the slot row. Late, never wrong — see editability's module note.
   */
  editableKeys: Set<NodeKey>;
}

/**
 * The value being edited, rendered as the string both modals take, or undefined when there
 * is none.
 *
 * Null and undefined collapse to the SAME answer here, which is why this was written as
 * `== null` and why that had to go: the modals' `currentAfeSourceId` / `currentVolumeSourceId`
 * are `string | undefined`, and "no value recorded" and "no value at all" are one state to
 * them. Spelling both out keeps that equivalence deliberate rather than incidental.
 *
 * @param {unknown} value - The first edited value, if the edit carries one.
 * @return {(string|undefined)} The value as a string, or undefined when absent.
 */
export function editedValueAsString(value: unknown): string | undefined {
  return value === null || value === undefined ? undefined : String(value);
}

/**
 * Groups the resolved slot inputs by layout zone (in {@link ZONE_ORDER}) and joins
 * each zone to its `DomainGroup` (when one exists) for the scenario / area context.
 * The zones with no domain (Type Curve, Volumes, Cashflow) still
 * render from `slotInputs` alone. FORMULAS ARE NO LONGER COLLECTED HERE: they moved to
 * the per-node rows (and the per-date rows) because AC 10's omission rule is per date and
 * cannot be expressed at zone granularity.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {MetaByNode} metaByNode - Served node definitions; carries `overridable`, which
 *   decides the edit affordance and arrives with the model map's own request.
 * @param {ZonesByKey} zonesByKey - Served zone metadata, used to tell a zone that has no
 *   domain from one whose DomainGroup is missing. Defaults to empty, which fails closed.
 * @param {boolean} registryAvailable - Whether the node registry has landed. Derived from
 *   `metaByNode` being non-empty; the volume-source classification is withheld without it.
 * @return {ZoneSection[]} The ordered sections to render.
 */
export function buildSections(
  response: SlotValuationInputsResponse,
  // DEFAULTS TO EMPTY for the export path (tearsheet-export.ts:299,:553), which renders no
  // edit affordance. An absent map reads as "nothing overridable" — the same late-arrival
  // semantics editability.ts documents for the model map's second request.
  metaByNode: MetaByNode = {},
  // Same defaulting, same reason: with no zone metadata the gate cannot tell a zone that has
  // no domain from one whose DomainGroup went missing, so it keeps failing closed.
  zonesByKey: ZonesByKey = {},
  // ⚠️ WHETHER THE NODE REGISTRY IS IN HAND, for the volume-source classification.
  //
  // DERIVED FROM `metaByNode` BY DEFAULT because emptiness is the signal: `useNodeMeta`
  // returns `byKey: {}` for both "not arrived yet" and a terminal failure, and the served
  // registry is never legitimately empty (221 nodes). So a non-empty map means it landed.
  // The caller may pass it explicitly; the export path relies on this default, and gets the
  // right answer because it only has a map at all once the request resolved.
  registryAvailable = Object.keys(metaByNode).length > 0
): ZoneSection[] {
  return buildSectionsWithBlocks(
    response,
    metaByNode,
    zonesByKey,
    registryAvailable
  ).sections;
}

/**
 * {@link buildSections}, also returning the source blocks the sections were cut from, so a
 * caller that needs both (the disclosed-series set) reuses them instead of walking the model
 * graph a second time.
 *
 * @param {SlotValuationInputsResponse} response - The tearsheet payload.
 * @param {MetaByNode} metaByNode - Served node definitions.
 * @param {ZonesByKey} zonesByKey - Served zone metadata.
 * @param {boolean} registryAvailable - Whether the node registry has landed.
 * @return {object} `{ sections, blocks }`.
 */
export function buildSectionsWithBlocks(
  response: SlotValuationInputsResponse,
  metaByNode: MetaByNode = {},
  zonesByKey: ZonesByKey = {},
  registryAvailable = Object.keys(metaByNode).length > 0
): { sections: ZoneSection[]; blocks: SourceBlock[] } {
  const domainByZone = new Map<ZoneKey, DomainGroup>();
  for (const domain of response.domains) {
    domainByZone.set(domain.zone, domain);
  }

  // ── AC 13 OWNS THESE ROWS; A ZONE TABLE MUST NOT RENDER THEM A SECOND TIME ────────
  //
  // The eight output rows appeared TWICE on the page — once in the Calculated result and
  // once as nodes of the `cashflow` zone. Worse
  // than untidy: the two renderings disagreed, because a zone row goes through
  // `formatNodeValue` (up to 8 decimals) and an output row through `formatOutputValue`
  // (none). The same NPV read "76,097.27021168" in one table and "76,097" in the other.
  //
  // Driven off the DECLARED list unioned with the payload's own keys, NOT off `kind` or
  // `zone`. Both of those would be far too broad: 43 of the 221 nodes are `kind: output`,
  // spread across all seven zones, and only these 8 are in `outputs.values`. Suppressing
  // by kind would delete the eight royalty/revenue monthly series and the five scheduled
  // date outputs — rows nobody has complained about and which nothing else shows.
  //
  // The union means a run whose payload OMITS irr (see outputRows) still
  // suppresses the zone row, so the page never shows a dash in one table and "Not computed
  // by this run" in another for the same node.
  const ownedByOutputs = outputsOwnedKeys(response);

  const visible = response.slotInputs.filter(
    (node) => !ownedByOutputs.has(node.key)
  );
  const byZone = new Map<ZoneKey, SlotInputNode[]>();
  for (const node of visible) {
    const list = byZone.get(node.zone) ?? [];
    list.push(node);
    byZone.set(node.zone, list);
  }

  // The derived source-block model. Classification runs over the FULL payload inside
  // buildSourceBlocks, so suppressing a row from the view cannot orphan its neighbours.
  // `metaByNode` doubles as the source lookup for the merged block's caption — the same
  // definitions the row trimming reads, so caption and rows cannot disagree about which
  // source produced them. Empty on the export path, which reads as `unknown_source`, which
  // is why buildExportRows passes its own meta in.
  const blocks = buildSourceBlocks(
    response,
    [...ownedByOutputs],
    (key) => metaByNode[key]?.source,
    registryAvailable
  );
  const ownershipExists = (byZone.get('ownership_interest')?.length ?? 0) > 0;
  const sections: ZoneSection[] = [];
  for (const zone of ZONE_ORDER) {
    const native = byZone.get(zone);
    if (!native || native.length === 0) {
      continue;
    }
    // Ownership Interest carries the working interest, relocated from its native (volumes)
    // block so the row shows once rather than in two blocks — the SAME helper the export
    // calls, so the page and the workbook agree on the block's rows. See
    // withOwnershipRelocation.
    const nodes = withOwnershipRelocation(
      zone,
      native,
      visible,
      ownershipExists
    );
    const domain = domainByZone.get(zone);
    // `provenance` is typed non-null by the contract, but the live payload can
    // ship `provenance: null` on a domain written before provenance existed — so
    // optional-chain it too (a bare `domain?.provenance.scenarioName` threw
    // "Cannot read properties of null" and crashed the tearsheet).
    const provenance = domain?.provenance ?? null;
    const block = blocks.find((b) => b.zone === zone);
    sections.push({
      zone,
      domain: domain?.domain ?? null,
      label: domain?.label ?? ZONE_LABELS[zone],
      scenario: provenance?.scenarioName ?? null,
      scenarioId: provenance?.scenarioId ?? null,
      area:
        provenance?.scenarioArea?.name ?? provenance?.caseArea?.name ?? null,
      provenance,
      nodes,
      scenarioHref: provenance?.scenarioHref ?? null,
      volumeSource: block?.volumeSource ?? null,
      groups: block?.groups ?? [],
      seriesColumns: seriesColumnsForZone(zone, response),
      // `nodes`, not `block.nodes`: identical sets today, but `nodes` is what this section
      // actually renders, and neither the marker nor a row control may claim an affordance
      // for something off screen.
      editableKeys: new Set(
        nodes
          .filter((node) =>
            isNodeEditable(metaByNode[node.key], domain, zonesByKey[zone])
          )
          .map((node) => node.key)
      ),
    });
  }
  return { sections, blocks };
}
